import React, { JSX, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChatOverlayView } from 'components/ChatOverlayView'
import { useEditorPreviewSource } from 'components/overlayPreview/EditorPreviewHub'
import { chatPreviewConfigFromSummary } from 'components/overlayPreview/previewConfig'
import {
  CHAT_LINE_REMOVE_FAILED,
  CHAT_LINE_REMOVE_RETRY,
  ChatLineRemovePane,
  ChatLineRemoval,
  ChatLineRemover,
  createChatLineRemover,
  removeChatLineVariablesOf
} from 'components/chatOverlays/chatLineRemove'
import { ChatGateCounts, ChatGateStatusLines, ChatGateStatusPane, EMPTY_CHAT_GATE_COUNTS } from 'components/chatOverlays/chatGateStatus'
import { graphqlRequest } from 'lib/helpers'
import { ChatOverlaySummary } from 'types/api'
import removeChatLineMutation from 'graphql/removeChatLine.graphql'

export const QUICK_PREVIEW_SAMPLE_LABEL = 'Sample'
export const QUICK_PREVIEW_LIVE_LABEL = 'Live'
export const QUICK_LIVE_VIEW_NOTE = "Live shows this overlay's real chat. Press the x on a line to remove it from your Quick Overlays."

export type QuickPreviewMode = 'sample' | 'live'

export const quickPreviewModeOf = ({
  offered,
  liveFor,
  chatId
}: {
  offered: boolean
  liveFor: string | null
  chatId: string | null
}): QuickPreviewMode =>
  offered && chatId != null && liveFor === chatId ? 'live' : 'sample'

const SWITCH_BUTTON = 'rounded px-2 py-0.5 text-[11px] font-semibold'
const SWITCH_ON = 'bg-brand text-black'
const SWITCH_OFF = 'text-dm-text-secondary hover:text-dm-text-primary'

export const QuickPreviewSwitch = ({
  mode,
  onChange
}: {
  mode: QuickPreviewMode
  onChange: (mode: QuickPreviewMode) => void
}): JSX.Element => (
  <div
    role='group'
    aria-label='Preview'
    data-testid='quick-preview-switch'
    className='inline-flex shrink-0 items-center gap-0.5 rounded-md border border-white/10 p-0.5'
  >
    <button
      type='button'
      aria-pressed={mode === 'sample'}
      data-testid='quick-preview-sample'
      onClick={() => onChange('sample')}
      className={`${SWITCH_BUTTON} ${mode === 'sample' ? SWITCH_ON : SWITCH_OFF}`}
    >
      {QUICK_PREVIEW_SAMPLE_LABEL}
    </button>
    <button
      type='button'
      aria-pressed={mode === 'live'}
      data-testid='quick-preview-live'
      onClick={() => onChange('live')}
      className={`${SWITCH_BUTTON} ${mode === 'live' ? SWITCH_ON : SWITCH_OFF}`}
    >
      {QUICK_PREVIEW_LIVE_LABEL}
    </button>
  </div>
)

export const createQuickChatLineRemover = ({
  tokenOf,
  onFailure,
  onSuccess,
  request = graphqlRequest
}: {
  tokenOf: () => string | undefined
  onFailure: () => void
  onSuccess: () => void
  request?: typeof graphqlRequest
}): ChatLineRemover => createChatLineRemover({
  send: async (removal: ChatLineRemoval) => {
    const token = tokenOf()
    if (token == null || token === '') throw new Error('signed out')
    return await request<{ StreamToolsRemoveChatLine: { removed: boolean } }>(
      removeChatLineMutation, removeChatLineVariablesOf(removal), token
    )
  },
  onFailure,
  onSuccess
})

export const QuickLivePanes = ({
  onRemove,
  onStatus,
  children
}: {
  onRemove: (removal: ChatLineRemoval) => void
  onStatus: (status: ChatGateCounts) => void
  children: ReactNode
}): JSX.Element => (
  <ChatGateStatusPane onStatus={onStatus}>
    <ChatLineRemovePane eventId={null} onRemove={onRemove}>
      {children}
    </ChatLineRemovePane>
  </ChatGateStatusPane>
)

const QuickChatLiveStage = ({ chat, heightPx }: { chat: ChatOverlaySummary, heightPx: number }): JSX.Element => {
  const source = useEditorPreviewSource('chat', chat.chatId)
  const previewConfig = useMemo(() => chatPreviewConfigFromSummary(chat), [chat])
  return (
    <div
      data-testid='quick-live-stage'
      data-chat-id={chat.chatId}
      className='overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-600 font-sans'
      style={{ height: heightPx, position: 'relative' }}
    >
      {source?.seam != null && (
        <ChatOverlayView
          chatId={chat.chatId}
          token={source.token ?? ''}
          fillHost
          preview={source.seam}
          previewConfig={previewConfig}
        />
      )}
    </div>
  )
}

export const QuickChatLiveView = ({
  chat,
  auth0Token,
  heightPx
}: {
  chat: ChatOverlaySummary
  auth0Token: string | undefined
  heightPx: number
}): JSX.Element => {
  const [removeFailed, setRemoveFailed] = useState(false)
  const [status, setStatus] = useState<ChatGateCounts>(EMPTY_CHAT_GATE_COUNTS)
  const tokenRef = useRef(auth0Token)
  tokenRef.current = auth0Token
  const remover = useMemo(() => createQuickChatLineRemover({
    tokenOf: () => tokenRef.current,
    onFailure: () => setRemoveFailed(true),
    onSuccess: () => setRemoveFailed(false)
  }), [])
  useEffect(() => () => remover.dispose(), [remover])
  const handleRemove = useCallback((removal: ChatLineRemoval): void => {
    remover.remove(removal)
  }, [remover])
  return (
    <div data-testid='quick-live-view'>
      <QuickLivePanes onRemove={handleRemove} onStatus={setStatus}>
        <QuickChatLiveStage chat={chat} heightPx={heightPx} />
      </QuickLivePanes>
      {removeFailed && (
        <div className='mt-2' role='alert' data-testid='chat-line-remove-failed'>
          <div className='flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
            <span className='flex-1'>{CHAT_LINE_REMOVE_FAILED}</span>
            <button
              type='button'
              onClick={() => { setRemoveFailed(false); remover.retry() }}
              className='shrink-0 rounded border border-red-400/40 px-2 py-0.5 font-semibold text-red-200 hover:bg-red-500/20'
            >
              {CHAT_LINE_REMOVE_RETRY}
            </button>
          </div>
        </div>
      )}
      <ChatGateStatusLines status={status} />
      <p className='mt-2 text-[11px] text-dm-text-accent' data-testid='quick-live-note'>{QUICK_LIVE_VIEW_NOTE}</p>
    </div>
  )
}
